import React, { useEffect, useMemo, useRef, useState } from 'react';

const CityAutocomplete = ({ formData, setFormData, disabled, field, citiesData, statesData }: any) => {
  const [inputValue, setInputValue] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const containerRef = useRef(null);

  const topCities = citiesData || [];

  const statesWithCities = useMemo(() => {
    const topCityIds = new Set(topCities.map((city: any) => city.id));
    return (
      statesData
        ?.map((state: any) => ({
          ...state,
          cities: state?.cities?.filter((city: any) => !topCityIds.has(city.id)),
        }))
        .filter((state: any) => state.cities.length > 0) || []
    );
  }, [statesData, topCities]);

  const cityOptions = useMemo(() => {
    const options: any = [];
    if (topCities.length > 0) {
      options.push({ label: 'Top Cities', options: topCities });
    }

    statesWithCities.forEach((state: any) => {
      options.push({
        label: state.state_name,
        options: state.cities,
      });
    });

    return options;
  }, [topCities, statesWithCities]);

  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return cityOptions;
    return cityOptions
      .map((group: any) => ({
        label: group.label,
        options: group.options.filter((city: any) => city.city_name.toLowerCase().includes(inputValue.toLowerCase())),
      }))
      .filter((group: any) => group.options.length > 0);
  }, [inputValue, cityOptions]);

  const selectedCity = useMemo(() => {
    return cityOptions.flatMap((group: any) => group.options).find((city: any) => city.id === formData.city);
  }, [formData.city, cityOptions]);

  const handleOptionClick = (city: any) => {
    const selectedState = statesData?.find((state: any) => state.cities?.some((c: any) => c.id === city.id));
    setFormData({
      ...formData,
      city: city.id,
      state: selectedState?.id || null,
    });
    setInputValue(city.city_name);
    setShowOptions(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !(containerRef.current as any).contains(event.target)) {
        setShowOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedCity) {
      setInputValue(selectedCity.city_name);
    }
  }, [selectedCity]);

  return (
    <div className="relative font-sans" ref={containerRef}>
      <div className="font-inter font-medium text-xs lg:text-sm text-[#404D61] px-1 flex items-center gap-x-2 mb-2">
        {field?.icon}
        <label className="text-sm lg:text-base">{field?.label}</label>
      </div>

      <input
        type="text"
        placeholder="Select city"
        value={inputValue}
        // onChange={(e) => setInputValue(e.target.value)}
        onChange={(e) => {
          const cleanedValue = e.target.value.replace(/^\s+/, '');
          setInputValue(cleanedValue);
        }}
        onFocus={() => !disabled && setShowOptions(true)}
        className="w-full px-4 py-2 text-sm border border-[#E1E3E6] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
        disabled={disabled}
        //  readOnly={formData?.course}
        required
      />

      {showOptions && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md max-h-64 overflow-y-auto shadow-lg text-sm">
          {filteredOptions.length === 0 ? (
            <div className="p-2 text-gray-500">No results found</div>
          ) : (
            filteredOptions.map((group: any) => (
              <div key={group.label}>
                <div className="px-3 py-1 bg-gray-100 text-gray-700 font-medium border-b border-gray-200">
                  {group.label}
                </div>
                {group.options.map((city: any) => (
                  <div
                    key={city.id}
                    className="px-4 py-2 hover:bg-blue-100 cursor-pointer"
                    onClick={() => handleOptionClick(city)}
                  >
                    {city.city_name}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CityAutocomplete;
